import { useMemo } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import type { LessonAiDraft } from "../../auth/api-client";
import { StudentIdeBody, type LessonRuntimeMarkDoneSpec } from "../../student-ide/StudentIdeBody";
import { StudentLessonAdvancedBody } from "../../student-ide/StudentLessonAdvancedBody";
import type { DemoTourStep } from "../../student-ide/demoTourSteps";
import {
  starterFilesFromMainPy,
  starterFilesToProjectFiles,
  syncMainPyInStarterFiles,
} from "../course-template-helpers";
import { buildLessonImmersiveTheme } from "../lessonImmersiveTheme";
import { lessonPreviewAppearanceQuery, teacherPreviewLessonUrl } from "../teacherPreviewLessonUrl";

export interface LessonPreviewPaneProps {
  readonly draft: LessonAiDraft;
  readonly savedLessonId: string | null;
}

export function LessonPreviewPane({ draft, savedLessonId }: LessonPreviewPaneProps) {
  const storageKey = useMemo(
    () => `ccs-teacher-ws-preview-${savedLessonId ?? "draft"}`,
    [savedLessonId]
  );

  const tourStepsForPreview: readonly DemoTourStep[] = useMemo(
    () =>
      draft.tourSteps.map((s) => ({
        title: s.title,
        body: s.body.length > 0 ? s.body : ["(No body text for this step.)"],
        referenceCode: s.referenceCode?.trim() ? s.referenceCode : undefined,
      })),
    [draft.tourSteps]
  );

  const previewTests: readonly LessonRuntimeMarkDoneSpec[] = useMemo(
    () =>
      draft.tests.map((t, i) => ({
        id: `draft-test-${i}`,
        name: t.name,
        testCode: t.testCode,
        isRequired: t.isRequired !== false,
        visibility: t.visibility ?? "detailed",
        description: t.description ?? null,
      })),
    [draft.tests]
  );

  const isAdvanced = (draft.workspaceMode ?? "simple") === "advanced";

  const previewInitialFiles = useMemo(() => {
    if (!isAdvanced) return [];
    if (draft.starterFiles?.length) {
      return syncMainPyInStarterFiles(draft.starterFiles, draft.mainPy);
    }
    return starterFilesToProjectFiles(starterFilesFromMainPy(draft.mainPy));
  }, [draft.mainPy, draft.starterFiles, isAdvanced]);

  const backTo = savedLessonId ? `/teacher/lessons/${savedLessonId}` : "/teacher/lessons/new";
  const fullPreviewUrl =
    savedLessonId && draft.title.trim()
      ? teacherPreviewLessonUrl({
          lessonId: savedLessonId,
          lessonTitle: draft.title,
          ...lessonPreviewAppearanceQuery(draft),
        })
      : null;

  const immersiveTheme = useMemo(
    () =>
      buildLessonImmersiveTheme({
        iconName: draft.iconName,
        iconBgColor: draft.iconBgColor,
        iconFgColor: draft.iconFgColor,
        workspaceMode: draft.workspaceMode,
        seed: savedLessonId ?? "draft",
      }),
    [
      draft.iconName,
      draft.iconBgColor,
      draft.iconFgColor,
      draft.workspaceMode,
      savedLessonId,
    ]
  );

  return (
    <div className="lesson-ws-preview">
      <div className="lesson-ws-preview-toolbar">
        <span className="lesson-ws-preview-label">Live student preview</span>
        {fullPreviewUrl ? (
          <Link to={fullPreviewUrl} className="btn secondary tight" target="_blank" rel="noreferrer">
            <FontAwesomeIcon icon={faUpRightFromSquare} /> Open full page
          </Link>
        ) : null}
      </div>
      <div className="lesson-ws-preview-body">
        {isAdvanced ? (
          <StudentLessonAdvancedBody
            eyebrow="Teacher · live preview"
            title={draft.title.trim() || "Untitled lesson"}
            backTo={backTo}
            backLabel="Editor"
            initialFiles={previewInitialFiles}
            filesStorageKey={storageKey}
            lessonTestsForMarkDone={previewTests}
            tourSteps={tourStepsForPreview}
            disableLocalPersist
            immersiveTheme={immersiveTheme}
          />
        ) : (
          <StudentIdeBody
            eyebrow="Teacher · live preview"
            title={draft.title.trim() || "Untitled lesson"}
            backTo={backTo}
            backLabel="Editor"
            initialMainPy={draft.mainPy}
            authoringStarterMainPy={draft.mainPy}
            storageKey={storageKey}
            tourSteps={tourStepsForPreview}
            lessonTestsForMarkDone={previewTests}
            disableLocalPersist
            immersiveTheme={immersiveTheme}
          />
        )}
      </div>
    </div>
  );
}
